import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../../api/fastapi";
import { z } from "zod";
import {
  AuditEvent,
  auditEventSchema,
  type AuditEventType,
} from "../../models/audit/auditEvent";
import { useAudit } from "../../contexts/auditContext";
import { controlsV2SessionApiBase } from "../../lib/sessionApiBase";

/** Envelope parsed loosely so each event can be validated individually. */
const feedEnvelopeSchema = z.object({
  events: z.array(z.unknown()),
  nextCursor: z.string().nullable(),
});

const PAGE_LIMIT = 50;

interface AuditEventFeedResult {
  events: AuditEvent[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

/**
 * Cursor-paginated audit event feed shared by the session and workspace
 * hooks below. Resets accumulated events + cursor whenever `filterKey`
 * changes (a stable string identity for the caller's filter set) or
 * `enabled` flips on, and dedupes by id when appending pages.
 *
 * A monotonically increasing request id guards against stale responses
 * landing after a filter change or unmount — the fragility the legacy
 * offset-based `useAuditLogs` reset dance was prone to.
 */
const useAuditEventFeed = (
  path: string,
  /** `[key, value]` pairs rather than a record, so repeated params like
   * `event_types` survive into the query string. */
  query: string[][],
  filterKey: string,
  enabled: boolean,
): AuditEventFeedResult => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const queryRef = useRef(query);
  queryRef.current = query;

  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageCursor: string | null, replace: boolean) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const requestQuery: string[][] = [
          ...queryRef.current,
          ["limit", String(PAGE_LIMIT)],
        ];
        if (pageCursor) {
          requestQuery.push(["cursor", pageCursor]);
        }

        const response = await fetchFastApi({
          method: "GET",
          path,
          query: requestQuery,
        });
        const data = feedEnvelopeSchema.parse(response);
        // Deployed bundles predate any newly added backend event type; the
        // strict enum would fail the whole page on one unknown event. Parse
        // per event instead and drop unrecognized shapes — the exhaustive
        // Records still force rebuilt clients to handle every known type.
        const parsedEvents = data.events.flatMap((candidate) => {
          const result = auditEventSchema.safeParse(candidate);
          if (!result.success) {
            console.warn("Dropping unrecognized audit event", result.error);
            return [];
          }
          return [result.data];
        });

        if (requestId !== requestIdRef.current) {
          return;
        }
        setEvents((prev) => {
          const base = replace ? [] : prev;
          const existingIds = new Set(base.map((event) => event.id));
          const deduped = parsedEvents.filter(
            (event) => !existingIds.has(event.id),
          );
          return [...base, ...deduped];
        });
        setCursor(data.nextCursor);
        setHasMore(data.nextCursor !== null);
      } catch (fetchError) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        console.error("Failed to fetch audit events", fetchError);
        setError("Failed to load audit events");
        toast.error("Failed to load audit events");
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [path],
  );

  // Full reset (events + cursor) whenever the filter identity changes or the
  // feed becomes enabled, then fetch the first page. Clearing on enabled: false
  // is intentional—drawer close wipes stale list; reopen refetches fresh data.
  useEffect(() => {
    // Invalidate any in-flight request so a late response can't repopulate
    // a list this reset just cleared (e.g. after the drawer closes).
    requestIdRef.current += 1;
    setEvents([]);
    setCursor(null);
    setHasMore(true);
    if (enabled) {
      fetchPage(null, true);
    }
    // Unmount is the same invalidation: a response landing after teardown
    // must not toast on whatever screen the user is on now.
    return () => {
      requestIdRef.current += 1;
    };
  }, [filterKey, enabled, fetchPage]);

  const loadMore = useCallback(() => {
    if (!enabled || loading || !hasMore) {
      return;
    }
    fetchPage(cursor, false);
  }, [enabled, loading, hasMore, cursor, fetchPage]);

  const refresh = useCallback(() => {
    setEvents([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(null, true);
  }, [fetchPage]);

  return { events, loading, error, hasMore, loadMore, refresh };
};

interface SessionAuditEventFilters {
  attributeTestColumnId?: string;
  sourceRowId?: string;
  /** Narrows the feed server-side; omit (or pass empty) for every type. */
  eventTypes?: AuditEventType[];
}

interface SessionAuditEventOptions {
  enabled?: boolean;
}

/**
 * Session-scoped audit event feed: `GET .../audit-log` on whichever router owns
 * the session, control or work step. Cell-filtered when `filters` is supplied —
 * the backend still includes session-level events per its
 * `(column matches OR event has no column) AND (row matches OR event has no
 * row)` semantics.
 *
 * The session kind comes from the audit provider rather than an argument: both
 * call sites (the work-paper Audit Log drawer and the attribute-test analysis
 * section) render inside one, and a control-only path here 404s silently on a
 * work step rather than failing loudly.
 */
export const useSessionAuditEvents = (
  controlSessionId: string,
  filters?: SessionAuditEventFilters,
  opts?: SessionAuditEventOptions,
): AuditEventFeedResult => {
  const { sessionKind } = useAudit();
  const attributeTestColumnId = filters?.attributeTestColumnId;
  const sourceRowId = filters?.sourceRowId;
  const enabled = (opts?.enabled ?? true) && controlSessionId.length > 0;
  // Joined into the filter identity below, so a new array of the same types
  // (a fresh `useMemo` on the caller's side) doesn't retrigger a refetch.
  const eventTypesKey = (filters?.eventTypes ?? []).join(",");

  const query = useMemo(() => {
    const q: string[][] = [];
    if (attributeTestColumnId) {
      q.push(["attribute_test_column_id", attributeTestColumnId]);
    }
    if (sourceRowId) {
      q.push(["source_row_id", sourceRowId]);
    }
    eventTypesKey
      .split(",")
      .filter((eventType) => eventType.length > 0)
      .forEach((eventType) => q.push(["event_types", eventType]));
    return q;
  }, [attributeTestColumnId, sourceRowId, eventTypesKey]);

  const filterKey = `${controlSessionId}|${attributeTestColumnId ?? ""}|${sourceRowId ?? ""}|${eventTypesKey}`;

  return useAuditEventFeed(
    `${controlsV2SessionApiBase(sessionKind, controlSessionId)}/audit-log`,
    query,
    filterKey,
    enabled,
  );
};

interface WorkspaceAuditEventFilters {
  actionSearch?: string;
  /** Concrete event types to filter to (the category tabs' mapping). */
  eventTypes?: AuditEventType[];
  startDate?: Date;
  endDate?: Date;
}

/**
 * Workspace-wide audit event feed: `GET /controls/v2/workspace/audit-log`.
 * Server-gated by the `view_audit_logs` permission.
 */
export const useWorkspaceAuditEvents = (
  filters: WorkspaceAuditEventFilters,
  opts?: { enabled?: boolean },
): AuditEventFeedResult => {
  const { actionSearch, eventTypes, startDate, endDate } = filters;
  const startDateIso = startDate ? startDate.toISOString() : undefined;
  const endDateIso = endDate ? endDate.toISOString() : undefined;
  const eventTypesKey = eventTypes?.join(",") ?? "";

  const query = useMemo(() => {
    const q: string[][] = [];
    if (actionSearch) {
      q.push(["action_search", actionSearch]);
    }
    for (const eventType of eventTypes ?? []) {
      q.push(["event_types", eventType]);
    }
    if (startDateIso) {
      q.push(["start_date", startDateIso]);
    }
    if (endDateIso) {
      q.push(["end_date", endDateIso]);
    }
    return q;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionSearch, eventTypesKey, startDateIso, endDateIso]);

  const filterKey = `${actionSearch ?? ""}|${eventTypesKey}|${startDateIso ?? ""}|${endDateIso ?? ""}`;

  return useAuditEventFeed(
    "/controls/v2/workspace/audit-log",
    query,
    filterKey,
    opts?.enabled ?? true,
  );
};
